import {
  type StepBuildCeremony,
  mergeStepBuildProps,
  pageBodyBlockBuildProps,
  sectionPageIntroMotions,
} from "@/components/benefits-discovery-v2/step-sequential-build";
import { track } from "@cakewalk/frontend-api";
import { KeyRound, Lock, Mail } from "@cakewalk/ui/icons";
import {
  V2Button,
  V2Card,
  V2CardContent,
  V2FormActions,
  V2FormField,
  V2Input,
  V2Typography,
} from "@cakewalk/ui/v2";
import { confirmResetPassword, resetPassword } from "aws-amplify/auth";
import { type FormEvent, useState } from "react";

/**
 * "Forgot your password?" step for company-census — mirrors the pulpo wallet
 * flow but in the census V2 design language.
 *
 * Two phases, both Cognito via Amplify v6:
 *   1. request  — enter email → `resetPassword` sends a confirmation code to the
 *      email on file.
 *   2. confirm  — enter the code + a new password → `confirmResetPassword`.
 * On success we surface a confirmation and route the user back to log in with
 * their new password.
 */

type Phase = "request" | "confirm" | "done";

// Keep this in step with Cognito's password policy: 8+ chars, upper, lower, and
// a number. A friendly client-side guard so the user isn't bounced by Cognito.
function isStrongPassword(value: string): boolean {
  return value.length >= 8 && /[a-z]/.test(value) && /[A-Z]/.test(value) && /\d/.test(value);
}

export function CompanyCensusForgotPasswordStep({
  defaultEmail,
  playBuild = false,
  ceremony = null,
  onBackToLogin,
}: {
  defaultEmail?: string;
  playBuild?: boolean;
  ceremony?: StepBuildCeremony | null;
  onBackToLogin: (email?: string) => void;
}) {
  const [phase, setPhase] = useState<Phase>("request");
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [maskedDestination, setMaskedDestination] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const { eyebrowMotion, titleMotion, descriptionMotion } = sectionPageIntroMotions(
    playBuild,
    ceremony,
  );

  const handleRequest = async (e: FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    setError(undefined);
    // tracking-plan: Flow 1B (login, mfa & password) > password_reset_started
    track("password_reset_started", { flow_name: "auth", step_name: "request_reset" });
    try {
      // The census app uses its own Cognito app client, which the pool's
      // CustomMessage Lambda keys on to email a bare reset code (entered on the
      // confirm screen below) rather than the owner-dashboard deep-link.
      const output = await resetPassword({ username: email.trim() });
      if (output.nextStep.resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE") {
        setMaskedDestination(output.nextStep.codeDeliveryDetails?.destination);
      }
      setPhase("confirm");
    } catch (err) {
      // Cognito throws LimitExceeded / UserNotFound etc. Surface the message but
      // don't confirm whether the account exists beyond what Cognito returns.
      // tracking-plan: Flow 11 (company_census) > census_password_reset_failed (P2)
      track("census_password_reset_failed", {
        flow_name: "auth",
        step_name: "request_reset",
        failure_reason: err instanceof Error ? err.message.slice(0, 80) : "unknown",
      });
      setError(err instanceof Error ? err.message : "Failed to send a reset code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async (e: FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    if (newPassword !== confirmPassword) {
      setError("Those passwords don't match.");
      return;
    }
    if (!isStrongPassword(newPassword)) {
      setError(
        "Use at least 8 characters with an uppercase letter, a lowercase letter, and a number.",
      );
      return;
    }
    setIsLoading(true);
    setError(undefined);
    try {
      await confirmResetPassword({
        username: email.trim(),
        confirmationCode: code.replace(/\s/g, ""),
        newPassword,
      });
      // tracking-plan: Flow 1B (login, mfa & password) > password_reset_completed
      track("password_reset_completed", { flow_name: "auth", step_name: "new_password_set" });
      setPhase("done");
    } catch (err) {
      // tracking-plan: Flow 11 (company_census) > census_password_reset_failed (P2)
      track("census_password_reset_failed", {
        flow_name: "auth",
        step_name: "new_password_set",
        failure_reason: err instanceof Error ? err.message.slice(0, 80) : "unknown",
      });
      setError(err instanceof Error ? err.message : "Failed to reset your password");
    } finally {
      setIsLoading(false);
    }
  };

  if (phase === "done") {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 py-12">
        <V2Card className="overflow-hidden border border-cw-v2-border shadow-cw-v2-card">
          <V2CardContent className="flex flex-col gap-6 p-6">
            <div className="flex flex-col gap-2">
              <V2Typography variant="overline" className="text-cw-v2-reliant-red">
                All set
              </V2Typography>
              <V2Typography variant="heading-medium" as="h1" className="font-semibold">
                Your password has been reset
              </V2Typography>
              <V2Typography variant="body" muted>
                You can now log in with your new password.
              </V2Typography>
            </div>
            <V2FormActions variant="stretch">
              <V2Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={() => onBackToLogin(email.trim())}
                data-track="button-company-census-reset-done-login"
              >
                Back to login
              </V2Button>
            </V2FormActions>
          </V2CardContent>
        </V2Card>
      </div>
    );
  }

  if (phase === "confirm") {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 py-12">
        <V2Card className="overflow-hidden border border-cw-v2-border shadow-cw-v2-card">
          <V2CardContent className="flex flex-col gap-6 p-6">
            <div className="flex flex-col gap-2">
              <V2Typography variant="overline" className="text-cw-v2-reliant-red">
                Check your email
              </V2Typography>
              <V2Typography variant="heading-medium" as="h1" className="font-semibold">
                Enter your code and a new password
              </V2Typography>
              <V2Typography variant="body" muted>
                We sent a reset code to {maskedDestination ?? "your email"}. Enter it below with
                your new password.
              </V2Typography>
            </div>

            <form onSubmit={handleConfirm} className="flex flex-col gap-5">
              <V2FormField label="Reset code">
                <V2Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  prefix={<KeyRound className="h-4 w-4" aria-hidden />}
                  data-sensitive
                />
              </V2FormField>

              <V2FormField label="New password">
                <V2Input
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  type="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  prefix={<Lock className="h-4 w-4" aria-hidden />}
                  data-sensitive
                />
              </V2FormField>

              <V2FormField label="Confirm new password" error={error}>
                <V2Input
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  type="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  prefix={<Lock className="h-4 w-4" aria-hidden />}
                  data-sensitive
                />
              </V2FormField>

              <V2FormActions variant="stretch">
                <V2Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={
                    isLoading ||
                    code.trim().length === 0 ||
                    newPassword.length === 0 ||
                    confirmPassword.length === 0
                  }
                  data-track="button-company-census-reset-confirm"
                >
                  {isLoading ? "Resetting password…" : "Reset password"}
                </V2Button>
              </V2FormActions>
            </form>

            <div className="flex flex-col items-center gap-1 border-t border-cw-v2-border/60 pt-5">
              <V2Button
                variant="link"
                onClick={() => {
                  setPhase("request");
                  setError(undefined);
                }}
                data-track="button-company-census-reset-resend"
              >
                Didn&rsquo;t get a code? Try again
              </V2Button>
            </div>
          </V2CardContent>
        </V2Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 py-12">
      <V2Card className="overflow-hidden border border-cw-v2-border shadow-cw-v2-card">
        <V2CardContent className="flex flex-col gap-6 p-6">
          <div className="flex flex-col gap-2">
            <V2Typography
              variant="overline"
              {...mergeStepBuildProps(eyebrowMotion, { className: "text-cw-v2-reliant-red" })}
            >
              Forgot your password?
            </V2Typography>
            <V2Typography
              variant="heading-medium"
              as="h1"
              {...mergeStepBuildProps(titleMotion, { className: "font-semibold" })}
            >
              Reset your password
            </V2Typography>
            <V2Typography variant="body" muted {...mergeStepBuildProps(descriptionMotion)}>
              Enter the email you use with Cakewalk and we&rsquo;ll send you a code to set a new
              password.
            </V2Typography>
          </div>

          <form
            onSubmit={handleRequest}
            className="flex flex-col gap-5"
            data-track="form-company-census-reset-request"
          >
            <div {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 0, ceremony))}>
              <V2FormField label="Email" error={error}>
                <V2Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  prefix={<Mail className="h-4 w-4" aria-hidden />}
                  data-sensitive
                />
              </V2FormField>
            </div>

            <V2FormActions
              variant="stretch"
              {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 1, ceremony))}
            >
              <V2Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={isLoading || email.trim().length === 0}
                data-track="button-company-census-reset-request"
              >
                {isLoading ? "Sending code…" : "Send reset code"}
              </V2Button>
            </V2FormActions>
          </form>

          <div className="flex flex-col items-center gap-1 border-t border-cw-v2-border/60 pt-5">
            <V2Typography variant="meta" muted>
              Remember your password?
            </V2Typography>
            <V2Button
              variant="link"
              onClick={() => onBackToLogin(email.trim() || undefined)}
              data-track="button-company-census-reset-back-to-login"
            >
              Back to login
            </V2Button>
          </div>
        </V2CardContent>
      </V2Card>

      <div className="flex items-center justify-center gap-2 text-cw-v2-text-muted">
        <Lock className="h-3.5 w-3.5" aria-hidden />
        <V2Typography variant="meta" muted>
          Encrypted &amp; secure. This is the same login you use for your existing benefits.
        </V2Typography>
      </div>
    </div>
  );
}
